import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class UserService {
  private http = inject(HttpClient);
  private currentUserSubject = new BehaviorSubject<User | null>(null);

  readonly currentUser$ = this.currentUserSubject.asObservable();

  emitUserFromLocalStorage(): void {
    const user = this.getCurrentUserSync();
    this.currentUserSubject.next(user ?? null);
  }

  applyUserJwt(userJwt: string, authToken?: string): void {
    if (authToken?.trim()) {
      localStorage.setItem('token', authToken.trim());
    }
    localStorage.setItem('userConnected', userJwt.trim());
    this.emitUserFromLocalStorage();
  }

  refreshCurrentUser(): Observable<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      return of(void 0);
    }

    // Collaborateur sessions come from loginMicrosoft with a full user JWT.
    // GET user/me is learner-only and returns 404 for staff.
    const existing = this.getCurrentUserSync();
    if (this.isCollaborateurUser(existing)) {
      this.emitUserFromLocalStorage();
      return of(void 0);
    }

    return this.http.get<{ user?: string }>(`${environment.apiUrl}user/me`).pipe(
      tap((response) => {
        if (this.isJwtString(response?.user)) {
          localStorage.setItem('userConnected', response.user.trim());
          this.emitUserFromLocalStorage();
        }
      }),
      map(() => void 0),
      catchError(() => of(void 0))
    );
  }

  getCurrentUserSync(): User | null {
    const raw = localStorage.getItem('userConnected');
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    try {
      console.log('jwtDecode', jwtDecode<User>(raw));
      return jwtDecode<User>(raw);
    } catch {
      localStorage.removeItem('userConnected');
      return null;
    }
  }

  private isJwtString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().split('.').length === 3;
  }

  private isCollaborateurUser(user: User | null): boolean {
    if (!user) {
      return false;
    }
    const accesses = Array.isArray(user.lmsAccess)
      ? user.lmsAccess
      : typeof user.lmsAccess === 'string' && user.lmsAccess.trim()
        ? [user.lmsAccess]
        : [];
    if (accesses.some((value) => typeof value === 'string' && value.trim().length > 0)) {
      return true;
    }
    const hints = [user.__t, user.type]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.toLowerCase());
    return hints.some((hint) => hint.includes('collabor'));
  }

  clear(): void {
    this.currentUserSubject.next(null);
  }
}
